/* eslint-disable no-implicit-coercion */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable react/jsx-curly-brace-presence */
import React, { useState, useEffect, useRef, useCallback, Suspense } from 'react';
import { Button, token } from '@bynder/design-system';
import { IconArrowLeft, IconArrowRight, IconCancel } from '@bynder/icons';
import { lazyPrefetching } from 'packages/helpers/helpers';
import * as persistVolume from 'packages/helpers/persistVolume';
import Loader from 'packages/common/loader/Loader';

export const VideoPlayer = lazyPrefetching(
    () =>
        import(
            /* webpackChunkName: "videojs" */
            /* webpackPrefetch: true */
            './Video'
        ),
);

export const AudioPlayer = lazyPrefetching(
    () =>
        import(
            /* webpackChunkName: "videojs" */
            /* webpackPrefetch: true */
            './Audio'
        ),
);

interface Props {
    src?: string;
    poster?: string;
    type?: string;
    carousel?: boolean;
    disabled?: any;
    prev?: any;
    next?: any;
    download?: any;
    onlyPoster?: boolean;
    alwaysFullScreen?: boolean;
    handleToggle?: () => void;
    [key: string]: any;
}

export default function Player(props: Props) {
    const {
        src,
        poster,
        type,
        carousel,
        disabled,
        prev,
        next,
        download,
        onlyPoster = false,
        alwaysFullScreen,
        handleToggle,
    } = props;

    const [fullScreen, setFullScreen] = useState(false);
    const [loaded, setLoaded] = useState(false);

    const volumeRef = useRef(+(persistVolume.getVolume() ?? 0.25));
    const isMutedRef = useRef(persistVolume.getMutedStatus());
    const isUnMountedRef = useRef(false);
    const fullScreenRef = useRef(false);

    const isFullScreen = alwaysFullScreen || fullScreen;
    fullScreenRef.current = isFullScreen;

    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (!fullScreenRef.current) return;
            if (e.keyCode === 37) {
                prev();
            }
            if (e.keyCode === 39) {
                next();
            }
        },
        [prev, next],
    );

    useEffect(() => {
        return () => {
            isUnMountedRef.current = true;
        };
    }, []);

    useEffect(() => {
        if (carousel) {
            document.addEventListener('keydown', handleKeyDown);
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [carousel, handleKeyDown]);

    useEffect(() => {
        setLoaded(false);
    }, [src]);

    const toggleFullScreen = useCallback(() => {
        const toggle =
            handleToggle ||
            (() => {
                setFullScreen((prev) => !prev);
            });
        toggle();
    }, [handleToggle]);

    const clickOutside = useCallback(
        (e: React.MouseEvent) => {
            if (e.target === e.currentTarget) {
                toggleFullScreen();
            }
        },
        [toggleFullScreen],
    );

    const handleLoad = useCallback(() => {
        if (isUnMountedRef.current) return;
        setLoaded(true);
    }, []);

    const handleVolumeChange = useCallback((volume, muted) => {
        persistVolume.setVolume(volume);
        persistVolume.setMutedStatus(muted);
    }, []);

    const showNextPrev = isFullScreen && carousel;

    return (
        <>
            <div className={`${isFullScreen ? 'modal-backdrop fade show' : ''}`} />
            <div className="h-100">
                <div
                    className={`${
                        isFullScreen ? 'modal player--modal fade show d-block p-5 text-center' : 'text-right h-100'
                    }`}
                    onClick={clickOutside}
                >
                    {(!loaded || !src) && (
                        <div className={`d-flex flex-column justify-content-center align-items-center h-100`}>
                            <Loader poster={poster} size={50} />
                            {!onlyPoster && <small className="mt-2">Loading media</small>}
                        </div>
                    )}
                    {isFullScreen && (
                        <Button
                            title="Close"
                            variant="clean"
                            // @ts-expect-error TODO(VBS-25739) Button className typing
                            className="position-absolute"
                            style={{ right: 15, top: 15 }}
                            onClick={toggleFullScreen}
                            icon={<IconCancel style={{ color: token.white }} />}
                        />
                    )}
                    {showNextPrev && (
                        <div
                            onClick={prev}
                            className={`fullscreen-arrow fullscreen-arrow-prev ${
                                disabled.prevButton ? 'disabled' : ''
                            }`}
                        >
                            <IconArrowLeft />
                        </div>
                    )}

                    {src && (
                        <div
                            className={`${
                                loaded
                                    ? `h-100 modal--player-wrapper ${
                                          isFullScreen ? 'd-inline-block position-relative full-screen' : ''
                                      }`
                                    : 'd-none'
                            }`}
                        >
                            {type === 'VIDEO' && (
                                <Suspense fallback={<div>Loading...</div>}>
                                    <VideoPlayer
                                        key={src}
                                        {...props}
                                        onFullScreen={toggleFullScreen}
                                        onPlayerReady={handleLoad}
                                        volume={volumeRef.current}
                                        isMuted={isMutedRef.current}
                                        onVolumeChange={handleVolumeChange}
                                    />
                                </Suspense>
                            )}
                            {type === 'AUDIO' && (
                                <Suspense fallback={<div>Loading...</div>}>
                                    <AudioPlayer
                                        key={src}
                                        {...props}
                                        onFullScreen={toggleFullScreen}
                                        onPlayerReady={handleLoad}
                                        volume={volumeRef.current}
                                        isMuted={isMutedRef.current}
                                        onVolumeChange={handleVolumeChange}
                                    />
                                </Suspense>
                            )}
                        </div>
                    )}
                    {showNextPrev && (
                        <div
                            onClick={next}
                            className={`fullscreen-arrow fullscreen-arrow-next ${
                                disabled.nextButton ? 'disabled' : ''
                            }`}
                        >
                            <IconArrowRight />
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
